import {
  FeatureProductsPaginatedResults,
  Top30ProductsPaginatedResults,
  PreFilledLogistics,
  BrandProduct,
  Competitor,
  Logistics,
  ProductMargin,
  Supplier,
  SupplierMargin,
  FeaturedProduct,
} from "~src/types/product";
import { ApiTypes, api } from "../common";

const PRODUCT_PATHS: ApiTypes.API = {
  featured: {
    path: `api/products/featured-products/`,
    method: ApiTypes.HTTPMethod.GET,
  },
  top30: {
    path: `api/products/top30-products/`,
    method: ApiTypes.HTTPMethod.GET,
  },
  feature: {
    path: `api/products/featured-products/`,
    method: ApiTypes.HTTPMethod.POST,
  },
  undefeature: {
    path: `api/products/featured-products/`,
    method: ApiTypes.HTTPMethod.DELETE,
  },
  detail: {
    path: "api/products/shopify-products-variants/",
    method: ApiTypes.HTTPMethod.GET,
  },
  margin: {
    path: "/api/report/product-margin-data/",
    method: ApiTypes.HTTPMethod.GET,
  },
  suppliers: {
    path: "api/report/product-suppliers/",
    method: ApiTypes.HTTPMethod.GET,
  },
  logistics: {
    path: "api/report/logistics/",
    method: ApiTypes.HTTPMethod.GET,
  },
  addLogistics: {
    path: "api/report/logistics/",
    method: ApiTypes.HTTPMethod.POST,
  },
  preFilledLogistics: {
    path: "api/prefill-logistics/",
    method: ApiTypes.HTTPMethod.POST,
  },
  competitors: {
    path: "/api/report/competitor-comparision-report/",
    method: ApiTypes.HTTPMethod.GET,
  },
  supplierMargin: {
    path: "api/report/margin-calculator/",
    method: ApiTypes.HTTPMethod.GET,
  },
  addSupplier: {
    path: "/api/report/product-suppliers/",
    method: ApiTypes.HTTPMethod.POST,
  },
  newCompetitor: {
    path: "api/report/brand-competitors/create-new-competitor/",
    method: ApiTypes.HTTPMethod.POST,
  },
  brandCompetitor: {
    path: "api/report/brand-competitors/",
    method: ApiTypes.HTTPMethod.POST,
  },
  getBrandCompetitor: {
    path: "api/report/brand-competitors/",
    method: ApiTypes.HTTPMethod.GET,
  },
  deleteSupplier: {
    path: "api/report/product-suppliers/",
    method: ApiTypes.HTTPMethod.DELETE,
  },
  updateProduct: {
    path: "api/products/update-shopify-product-variant/",
    method: ApiTypes.HTTPMethod.PATCH,
  },
  deleteCompetitor: {
    path: "api/report/brand-competitors/",
    method: ApiTypes.HTTPMethod.DELETE,
  },
};

export const deleteCompetitor = async (brandId: number | undefined) => {
  const endpoint = PRODUCT_PATHS.deleteCompetitor;

  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}/`,
  });
  return response.data;
};

export const editProduct = async (
  productId: number | string,
  formData: any,
) => {
  const endpoint = PRODUCT_PATHS.updateProduct;

  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${productId}/`,
    data: formData,
  });

  return response.data;
};

export const brandCompetitor = async (payload: {
  competitor_brands_ids: string[];
  target_brand_id: number | undefined;
}) => {
  const endpoint = PRODUCT_PATHS.brandCompetitor;

  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: payload,
  });
  return response.data;
};

export const deleteSupplier = async (supplier_id: number) => {
  const endpoint = PRODUCT_PATHS.deleteSupplier;

  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${supplier_id}/`,
  });

  return response.data;
};

export const addCompetitor = async (formData: Record<string, unknown>) => {
  const endpoint = PRODUCT_PATHS.newCompetitor;

  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: formData,
  });
  return response.data;
};

export const addSupplier = async (formData: any) => {
  const endpoint = PRODUCT_PATHS.addSupplier;

  const response = await api<BrandProduct[]>({
    method: endpoint.method,
    url: endpoint.path,
    data: formData,
  });

  return response.data;
};

export const getFeaturedProducts = async (
  page: number,
  page_size: number,
  brand_id?: number,
  options?: { signal?: AbortSignal }
) => {
  const endpoint = PRODUCT_PATHS.featured;

  const response = await api<FeatureProductsPaginatedResults>({
    method: endpoint.method,
    url: `${endpoint.path}?page=${page}&page_size=${page_size}`,
    params: { brand_id },
    signal: options?.signal
  });
  return response.data;
};

export const getTop30Products = async (
  page: number,
  page_size: number,
  brand_id?: number,
  options?: { signal?: AbortSignal }
) => {
  const endpoint = PRODUCT_PATHS.top30;
  const response = await api<Top30ProductsPaginatedResults>({
    method: endpoint.method,
    url: endpoint.path,
    params: {
      page,
      page_size,
      ...(brand_id && { brand_id }),
    },
    ...options,
  });
  return response;
};

export const getCurrentMonthTop30 = async (
  brand_id?: number,
  options?: { signal?: AbortSignal }
) => {
  const endpoint = PRODUCT_PATHS.top30;
  const response = await api<Top30ProductsPaginatedResults>({
    method: endpoint.method,
    url: endpoint.path,
    params: {
      action: 'current-month',
      ...(brand_id && { brand_id }),
    },
    ...options,
  });
  return response;
};

export const addToTop30 = async (
  product_variant_id: number,
  options?: { signal?: AbortSignal }
) => {
  const endpoint = PRODUCT_PATHS.top30;
  const response = await api<{ success: boolean; message: string; top30_rank: number }>({
    method: 'POST',
    url: endpoint.path,
    data: {
      action: 'add',
      product_variant_id,
    },
    ...options,
  });
  return response;
};

export const removeFromTop30 = async (
  product_variant_id: number,
  options?: { signal?: AbortSignal }
) => {
  const endpoint = PRODUCT_PATHS.top30;
  const response = await api<{ success: boolean; message: string; remaining_count: number }>({
    method: 'POST',
    url: endpoint.path,
    data: {
      action: 'remove',
      product_variant_id,
    },
    ...options,
  });
  return response;
};

export const featureProduct = async (product_variant_id?: number | string) => {
  const endpoint = PRODUCT_PATHS.feature;

  const response = await api<BrandProduct[]>({
    method: endpoint.method,
    url: endpoint.path,
    data: { product_variant_id },
  });

  return response.data;
};

export const unFeatureProduct = async (product_variant_id?: number | string) => {
  const endpoint = PRODUCT_PATHS.undefeature;

  const response = await api<BrandProduct[]>({
    method: endpoint.method,
    url: endpoint.path,
    data: { product_variant_id },
  });

  return response.data;
};

export const getProductDetail = async (
  productVariatnId: string | undefined,
) => {
  const endpoint = PRODUCT_PATHS.detail;
  const response = await api<FeaturedProduct>({
    method: endpoint.method,
    url: endpoint.path,
    params: { product_variant_id: productVariatnId },
  });
  return response.data;
};

export const getProductMargin = async (
  product_variant_id: number | undefined | string,
) => {
  const endpoint = PRODUCT_PATHS.margin;
  const response = await api<ProductMargin>({
    method: endpoint.method,
    url: endpoint.path,
    params: { product_variant_id },
  });
  return response.data;
};

export const getProductSuppliers = async (
  product_variant_id: number | undefined | string,
) => {
  const endpoint = PRODUCT_PATHS.suppliers;
  const response = await api<{ data: { results: Supplier[] } }>({
    method: endpoint.method,
    url: endpoint.path,
    params: { product_variant_id },
  });

  return response.data.data.results;
};

export const getProductCompetitorsReport = async (
  brand_id: number | string | undefined,
) => {
  const endpoint = PRODUCT_PATHS.getBrandCompetitor;
  const response = await api<Competitor[]>({
    method: endpoint.method,
    url: endpoint.path,
    params: { brand_id },
  });

  return response.data;
};

export const getProductCompetitors = async (
  brand_id: number | string | undefined,
  competitor_id?: number | string | undefined,
) => {
  const endpoint = PRODUCT_PATHS.competitors;
  const response = await api<Competitor[]>({
    method: endpoint.method,
    url: endpoint.path,
    params: { brand_id, competitor_id },
  });

  return response.data;
};

export const getLogistics = async (
  product_id: string,
  product_supplier: number,
) => {
  const endpoint = PRODUCT_PATHS.logistics;
  const response = await api<Logistics[]>({
    method: endpoint.method,
    url: endpoint.path,
    params: { product_id, product_supplier },
  });

  return response.data;
};

export const addLogistics = async (formData: Logistics) => {
  const endpoint = PRODUCT_PATHS.addLogistics;
  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: formData,
  });

  return response.data;
};

export const getPrefilledLogistics = async (
  supplier_id: number,
  product_id: string,
) => {
  const endpoint = PRODUCT_PATHS.preFilledLogistics;
  const response = await api<PreFilledLogistics>({
    method: endpoint.method,
    url: endpoint.path,
    data: { supplier_id, product_id },
  });

  return response.data;
};

export const getSupplierMargin = async (params: any) => {
  const endpoint = PRODUCT_PATHS.supplierMargin;
  const response = await api<SupplierMargin[]>({
    method: endpoint.method,
    url: endpoint.path,
    params,
  });

  return response.data;
};
